import { Clipboard } from "@angular/cdk/clipboard";
import { BreakpointObserver } from "@angular/cdk/layout";
import { CommonModule } from "@angular/common";
import { Component, computed, inject, Input, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { ContactPersonFormComponent } from "@components/project-forms/contact-person-form/contact-person-form.component";
import { CustomerFormComponent } from "@components/project-forms/customer-form/customer-form.component";
import { GoodsRecipientFormComponent } from "@components/project-forms/goods-recipient/goods-recipient-form.component";
import { ProjectFormComponent } from "@components/project-forms/project-form/project-form.component";
import { ProjectFormsService } from "@components/project-forms/project-forms.service";
import type { ScopedProject } from "@domain/project/project-guards";
import { isFgdsProject, isMonitoringProject } from "@domain/project/project-guards";
import { ProjectService } from "@domain/project/project.service";
import { TemplateDto } from "@domain/project/template";
import { TemplateService } from "@domain/project/template/template.service";
import { UserService } from "@domain/user/user.service";
import { DropdownOptions } from "@odx/angular/components/dropdown";
import { ToastHelper } from "@utils/toast-helper.service";
import { combineLatest, EMPTY, filter, map, Observable, of, switchMap } from "rxjs";
import { Breakpoints } from "../../../../styles/breakpoints";

@Component({
  selector: "app-project-information",
  templateUrl: "./project-information.component.html",
  styleUrls: ["./project-information.component.scss"],
  imports: [
    UiKitModule,
    ReactiveFormsModule,
    ProjectFormComponent,
    CustomerFormComponent,
    ContactPersonFormComponent,
    GoodsRecipientFormComponent,
    CommonModule,
  ],
  standalone: true,
})
export class ProjectInformationComponent implements OnInit, OnDestroy {
  private userService = inject(UserService);
  private formBuilder = inject(FormBuilder);
  private projectFormsService = inject(ProjectFormsService);
  private projectService = inject(ProjectService);
  private templateService = inject(TemplateService);
  private breakpointObserver = inject(BreakpointObserver);
  private readonly normalizeOptionalString = (value: unknown) => this.projectFormsService.normalizeOptionalString(value);
  private toastHelper = inject(ToastHelper);
  private clipboard = inject(Clipboard);
  protected readonly isOpportunityLinkVisible = signal(false);
  opportunityLink: string | null = null;
  protected copyProjectLinkTooltip: string = $localize`:@@project.btn.copy.share.link:Kopiert einen teilbaren Link zu diesem Projekt. Die URL in der Adressleiste des Browsers kann aktuell aus technischen Gründen nicht zum Teilen verwendet werden.`;
  protected isMobile$ = this.breakpointObserver.observe(Breakpoints.mobile).pipe(map((result) => result.matches));
  protected projectSignal = signal<ScopedProject | undefined>(undefined);
  dropdownOptions: Partial<DropdownOptions> = {};

  @Input() set projectData(project: ScopedProject) {
    this.project = project;
    this.projectFormsService.initializeProject(this.project);
    this.initSwitchForm();
    this.projectSignal.update(() => this.project);
    this.updateLinkVisibility();
  }
  private updateLinkVisibility(): void {
    const link = this.project?.opportunityLink;
    this.isOpportunityLinkVisible.set(typeof link === "string" && link.trim().length > 0);
  }
  isWriteable = signal(false);
  editEnabled: boolean = false;
  goodsRecipientForm?: FormGroup;
  // Needed to show validation errors on submit without userinput
  @ViewChild("projectFormComponent")
  projectFormComponent!: ProjectFormComponent;
  @ViewChild("customerFormComponent")
  customerFormComponent!: CustomerFormComponent;
  @ViewChild("contactPersonFormComponent")
  contactPersonFormComponent!: ContactPersonFormComponent;
  @ViewChild("goodsRecipientFormComponent")
  goodsRecipientFormComponent!: GoodsRecipientFormComponent;

  switchForm!: FormGroup;
  protected isReadOnlyUser$: Observable<boolean>;
  protected hasWritePermission$: Observable<boolean | undefined>;
  protected sharedProjectLink: string | undefined;

  private project!: ScopedProject;

  private template$ = combineLatest([this.templateService.projectTemplate$, this.userService.businessScope$]).pipe(
    switchMap(([projectTemplate, scope]) => {
      if (!scope) return EMPTY;
      return projectTemplate ? of(projectTemplate) : this.templateService.getGlobalTemplate(scope);
    }),
    filter((template): template is TemplateDto => !!template),
  );

  protected readonly template = toSignal(this.template$, {
    initialValue: null,
  });

  templateFields = computed(() => {
    const template = this.template();
    if (!template) return null;
    return template.properties?.project ?? null;
  });

  protected isGoodsRecipientInformationVisible = computed<boolean | undefined>(() => {
    const template = this.template();
    return template?.properties?.project?.details?.goodsRecipient?.information?.visible;
  });

  protected readonly isShowPricesVisible = computed(() => {
    const template = this.template();
    if (template) return template.properties?.project?.details?.general?.showPrices?.visible;
    return false;
  });

  protected readonly clearFormSignal = signal<boolean>(false);
  constructor() {
    this.isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
    this.hasWritePermission$ = this.projectService.hasUserWritePermission$.pipe(
      filter((val): val is boolean => val !== undefined),
    );
  }
  ngOnDestroy(): void {
    this.projectFormsService.resetProject(this.project!);
    this.clearFormSignal.update(() => true);
  }

  ngOnInit(): void {
    if (!this.project) return;
    this.sharedProjectLink = this.project.cloudId ? this.projectService.getSharedProjectLink(this.project.cloudId) : undefined;
  }

  onEdit() {
    if (!this.editEnabled) {
      this.editEnabled = true;
      return;
    }
    this.onReset();
    this.editEnabled = false;
  }

  onSubmit() {
    this.projectFormComponent?.triggerSubmit();
    this.customerFormComponent?.triggerSubmit();
    this.contactPersonFormComponent?.triggerSubmit();
    this.goodsRecipientFormComponent?.triggerSubmit();
    if (
      !this.projectFormComponent.projectForm?.valid ||
      !this.customerFormComponent.customerForm?.valid ||
      !this.contactPersonFormComponent.contactPersonForm?.valid ||
      (this.goodsRecipientForm && !this.goodsRecipientForm.valid)
    ) {
      return;
    }
    this.mapProject();
    this.projectService.update(this.project).subscribe();
    this.editEnabled = false;
    this.updateLinkVisibility();
  }
  toggleEdit(): void {
    if (this.editEnabled) {
      this.onSubmit();
    } else {
      this.onEdit();
    }
  }

  private onReset() {
    this.projectFormsService.resetProject(this.project!);
    this.editEnabled = false;
  }

  private mapProject() {
    const projectForm = this.projectFormComponent.projectForm;
    this.project!.name = projectForm.value.name!;
    this.project!.version = this.normalizeOptionalString(projectForm.value.version);
    this.project!.status = projectForm.value.status ?? "";
    this.project!.placeName = this.normalizeOptionalString(projectForm.value.placeName);
    if (isFgdsProject(this.project!)) {
      this.project.forzaId = this.normalizeOptionalString(projectForm.value.forzaId);
      this.project.region = projectForm.value.region;
    }
    if (isMonitoringProject(this.project!)) {
      this.project.draegerContactPerson = this.normalizeOptionalString(projectForm.value.draegerContactPerson);
    }
    this.project!.opportunityId = this.normalizeOptionalString(projectForm.value.opportunityId);
    this.project!.opportunityLink = this.normalizeOptionalString(projectForm.value.opportunityLink);
    this.project!.notes = this.normalizeOptionalString(projectForm.value.notes);

    const customerForm = this.customerFormComponent.customerForm;
    this.project!.customer!.name = this.normalizeOptionalString(customerForm?.value.name);
    this.project!.customer!.customerNumber = this.normalizeOptionalString(customerForm?.value.customerNumber);
    this.project!.customer!.street = this.normalizeOptionalString(customerForm?.value.street);
    this.project!.customer!.zipCode = this.normalizeOptionalString(customerForm?.value.zipCode);
    this.project!.customer!.city = this.normalizeOptionalString(customerForm?.value.city);

    const contactPersonForm = this.contactPersonFormComponent.contactPersonForm;
    this.project!.contactPerson!.firstname = this.normalizeOptionalString(contactPersonForm?.value.firstname);
    this.project!.contactPerson!.lastname = this.normalizeOptionalString(contactPersonForm?.value.lastname);
    this.project!.contactPerson!.department = this.normalizeOptionalString(contactPersonForm?.value.department);
    this.project!.contactPerson!.email = this.normalizeOptionalString(contactPersonForm?.value.email);
    this.project!.contactPerson!.phoneNumber = this.normalizeOptionalString(contactPersonForm?.value.phoneNumber);

    if (this.goodsRecipientForm) {
      this.project!.goodsRecipient!.information = this.normalizeOptionalString(this.goodsRecipientForm?.value.information);
    }

    this.project!.lastModified = new Date();
  }

  private initSwitchForm(): void {
    if (!isFgdsProject(this.project)) return;
    this.switchForm = this.formBuilder.group({
      switch: [this.project.showCostFlag],
    });
    this.switchForm.valueChanges.subscribe(() => {
      if (!isFgdsProject(this.project)) return;
      this.project.showCostFlag = this.switchForm.value.switch;
      this.project.lastModified = new Date();
      this.projectService.update(this.project).subscribe();
    });
  }

  openLink(): void {
    const link = this.project?.opportunityLink;
    if (link) {
      window.open(link, "_blank");
    }
  }
  protected copyProjectLink() {
    const success = !!this.sharedProjectLink && this.clipboard.copy(this.sharedProjectLink);

    this.toastHelper[success ? "success" : "error"](
      success
        ? $localize`:@@toast.project.linkToClipboard.success: Der Link zum Projekt wurde in die Zwischenablage kopiert`
        : $localize`:@@toast.project.link.copied: Der Link zum Projekt konnte nicht in die Zwischenablage kopiert werden.`,
    );
  }
}
